// Layer Daily Reports' action-time shared-projection reconciliation owner.
//
// App's legacy boot still provides these two projections while the Layer
// surfaces remain boot-dependent. This module owns only the post-mutation and
// post-restore refresh: Home needs the active 45-day rows and Layers Home needs
// the complete active history. A restore RPC exposes no report row, so both
// projections must be read back as full rows rather than inferred from its
// success envelope.
import React from 'react';

import {sb} from '../lib/supabase.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import {addDays, toISO} from '../lib/dateUtils.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import {useDailysRecent} from '../contexts/DailysRecentContext.jsx';
import {useLayer} from '../contexts/LayerContext.jsx';

export const LAYER_DAILYS_RECENT_WINDOW_DAYS = 45;
const PAGE = 1000;

// The physical reads select `*`, and the contexts intentionally describe raw
// table rows as unknown[]. This module establishes only that each result is a
// row object; it does not invent a database row interface just to shuttle rows
// between the two established consumers.
export type LayerDailyProjectionRow = Record<string, unknown>;

export type LayerDailysProjection = {
  recent: LayerDailyProjectionRow[];
  all: LayerDailyProjectionRow[];
};

export type LayerDailysProjectionRefresh = () => Promise<LayerDailysProjection | null>;

type LayerDailysReadResult = {
  data: unknown[] | null;
  error: {message?: string} | null;
};

export type LayerDailysProjectionReadOptions = {
  now?: Date;
  signal?: AbortSignal;
};

export type LayerDailysProjectionSetters = {
  setRecent: (rows: LayerDailyProjectionRow[]) => void;
  setAll: (rows: LayerDailyProjectionRow[]) => void;
};

type MaybeAbortable<Builder> = {then: unknown; abortSignal?: (signal: AbortSignal) => Builder};

function withAbortSignal<Builder extends MaybeAbortable<Builder>>(builder: Builder, signal?: AbortSignal): Builder {
  return signal && typeof builder.abortSignal === 'function' ? builder.abortSignal(signal) : builder;
}

export function layerDailysRecentCutoff(now: Date = new Date()): string {
  // Mirrors the existing legacy boot sidecar exactly: calendar days, inclusive
  // `gte`, then newest report date first.
  return toISO(addDays(now, -LAYER_DAILYS_RECENT_WINDOW_DAYS));
}

/**
 * Reads Home's exact active 45-day projection. `null` means the transport
 * resolved without a payload, which is deliberately distinct from a real
 * successful empty set: absent payloads must retain last-good context rows.
 */
export async function loadLayerDailysRecent(
  client: SupabaseClient,
  {now = new Date(), signal}: LayerDailysProjectionReadOptions = {},
): Promise<LayerDailyProjectionRow[] | null> {
  const query = client
    .from('layer_dailys')
    .select('*')
    .is('deleted_at', null)
    .gte('date', layerDailysRecentCutoff(now))
    .order('date', {ascending: false});
  const result = (await withAbortSignal(query, signal)) as LayerDailysReadResult;
  if (result.error) throw new Error('layer_dailys recent: ' + (result.error.message || String(result.error)));
  return Array.isArray(result.data) ? (result.data as LayerDailyProjectionRow[]) : null;
}

/**
 * Reads the complete active history for Layers Home. Every page is required:
 * a failed or absent page returns no projection at all, never the partial rows
 * accumulated before it. This is the important difference between a true
 * action-time refresh and a plausible-looking partial restore projection.
 */
export async function loadAllLayerDailys(
  client: SupabaseClient,
  {signal}: LayerDailysProjectionReadOptions = {},
): Promise<LayerDailyProjectionRow[] | null> {
  const rows: LayerDailyProjectionRow[] = [];
  let offset = 0;
  while (true) {
    const query = client
      .from('layer_dailys')
      .select('*')
      .is('deleted_at', null)
      .order('date', {ascending: false})
      .range(offset, offset + PAGE - 1);
    const result = (await withAbortSignal(query, signal)) as LayerDailysReadResult;
    if (result.error) throw new Error('layer_dailys full history: ' + (result.error.message || String(result.error)));
    if (!Array.isArray(result.data)) return null;
    rows.push(...(result.data as LayerDailyProjectionRow[]));
    if (result.data.length < PAGE) return rows;
    offset += PAGE;
  }
}

/**
 * One coherent action-time publication for the two consumers. A caller never
 * publishes a new Home recent value paired with a stale full dashboard history
 * (or vice versa): both reads must succeed with concrete payloads before either
 * can publish. The separate queries are not represented as a database
 * transaction snapshot.
 */
export async function loadLayerDailysProjection(
  client: SupabaseClient,
  options: LayerDailysProjectionReadOptions = {},
): Promise<LayerDailysProjection | null> {
  const [recent, all] = await Promise.all([
    loadLayerDailysRecent(client, options),
    loadAllLayerDailys(client, options),
  ]);
  if (recent === null || all === null) return null;
  return {recent, all};
}

/**
 * Fail-soft publication boundary. Errors deliberately propagate to the action
 * caller so it can observe/settle them, but neither context is cleared or
 * partially replaced on any failed read.
 */
export async function refreshLayerDailysProjection(
  client: SupabaseClient,
  {setRecent, setAll}: LayerDailysProjectionSetters,
  options: LayerDailysProjectionReadOptions = {},
): Promise<LayerDailysProjection | null> {
  const projection = await loadLayerDailysProjection(client, options);
  if (projection === null) return null;
  setRecent(projection.recent);
  setAll(projection.all);
  return projection;
}

/**
 * Auth/session-safe action callback shared by the Layer index, record page,
 * authenticated webform, and Recently Deleted. It gives every successful Layer
 * write/restore the same full-row owner while ensuring a stale handler cannot
 * issue or publish a projection for a replaced subject or an unmounted route/
 * panel.
 */
export function useLayerDailysProjectionRefresh(): () => Promise<LayerDailysProjection | null> {
  const {authState, sessionEpoch} = useAuth();
  const {setLayerDailysRecent} = useDailysRecent();
  const {setAllLayerDailys} = useLayer();
  const subjectId = authState ? authState.user?.id || null : null;
  const sessionScope = subjectId ? `${sessionEpoch}:${subjectId}` : null;
  const currentSessionScopeRef = React.useRef<string | null>(sessionScope);
  currentSessionScopeRef.current = sessionScope;
  const requestRef = React.useRef(0);
  const activeControllerRef = React.useRef<AbortController | null>(null);
  const isLiveRef = React.useRef(false);

  React.useLayoutEffect(() => {
    isLiveRef.current = true;
    return () => {
      isLiveRef.current = false;
      requestRef.current += 1;
      activeControllerRef.current?.abort();
      activeControllerRef.current = null;
    };
  }, [sessionScope]);

  return React.useCallback(async () => {
    const requestSessionScope = sessionScope;
    if (!requestSessionScope || !isLiveRef.current || requestSessionScope !== currentSessionScopeRef.current)
      return null;

    // A newer mutation/restore has a more complete snapshot. Abort the older
    // request so it cannot publish over it after a slower full-history page.
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    activeControllerRef.current?.abort();
    const controller = new AbortController();
    activeControllerRef.current = controller;
    try {
      return await refreshLayerDailysProjection(
        sb,
        {
          setRecent: (rows) => {
            if (
              controller.signal.aborted ||
              requestId !== requestRef.current ||
              requestSessionScope !== currentSessionScopeRef.current
            ) {
              return;
            }
            setLayerDailysRecent(rows);
          },
          setAll: (rows) => {
            if (
              controller.signal.aborted ||
              requestId !== requestRef.current ||
              requestSessionScope !== currentSessionScopeRef.current
            ) {
              return;
            }
            setAllLayerDailys(rows);
          },
        },
        {signal: controller.signal},
      );
    } finally {
      if (activeControllerRef.current === controller) activeControllerRef.current = null;
    }
  }, [sessionScope, setLayerDailysRecent, setAllLayerDailys]);
}
